import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { classMap } from 'lit/directives/class-map.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import { userManager } from '../../store/auth/userManager.js';
import { closePreview } from '../../store/preview/preview.js';
import { launchTypes } from '../../store/elearning/helpers.js';
import '@sdb-develop/learning-os-player/sdb-os-player.js';
import '@sdb-develop/learning-frontend';
import '@polymer/paper-ripple';
import '../scorm/sdb-scorm-preview-player.js';
import './sdb-preview-url-player.js';

@customElement('sdb-preview-dialog')
class SdbPreviewDialog extends connect(store)(LitElement) {

    @property({ type: Boolean })
    accessor backdrop = true;

    @property({ type: Boolean })
    accessor transparent;

    @state()
    accessor user;

    @state()
    accessor opened;

    @state()
    accessor playerType;

    @state()
    accessor launchData;

    @state()
    accessor launchUrl;

    constructor() {
        super();

        window.addEventListener('message', msg => {
            switch (msg.data) {
                case 'close-preview':
                    store.dispatch(closePreview());
                    break;
            }
        });
    }

    stateChanged(state) {
        this.user = state.auth.user;
        this.opened = state.preview?.popupOpen === true;
        this.playerType = state.preview?.playerType;
        this.launchType = state.preview?.launchType;
        this.launchData = state.preview?.launchData;
        this.launchUrl = state.preview?.popupOpen === true ? state.preview.launchUrl : 'about:blank';
        this.autoClose = state.preview?.popupOpen === true ? !!state.preview.autoClose : true;
    }

    static get styles() {
        return css`
            div#canvas:not([opened]) {
                display: none;
            }

            div#canvas[opened] {
                display: block;
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 1301;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
            }

            div#canvas[opened][backdrop] {
                background: rgba(0, 0, 0, .3);
            }

            div#preview_dialog {
                position: relative;
                background: #fff;
                width: 90%;
                height: 90%;
                box-shadow: rgba(0, 0, 0, 0.16) 0px 10px 36px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px;
                animation: zoomIn 0.3s;
            }

            div#preview_dialog.transparent {
                background: transparent;
                box-shadow: none;
            }

            div#preview_dialog.new-window {
                width: 40%;
                height: 30%;
            }

            iframe, sdb-os-player, sdb-scorm-preview-player {
                width: 100%;
                height: 100%;
                border: none;
                margin:0;
                padding:0;
            }

            div.close {
                position: absolute;
                width: 40px;
                height: 40px;
                right: -30px;
                top: -30px;
                background: white;
                border: 1px solid #ededed;
                border-radius: 50%;
                padding: 0;
                margin: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 3px 1px -2px rgba(0, 0, 0, 0.2);
            }

            iron-icon {
                --iron-icon-width: 24px;
                --iron-icon-height: 24px;
            }

            div.close[hidden] {
                display: none;
            }

            @media only screen and (max-width: 768px) {
                div#preview_dialog.autoclose {
                    width: 100%;
                    min-width: 100%;
                    height: 100%;
                    min-height: 100%;
                }
            }

            @keyframes zoomIn {
                0% {
                    transform: scale(0);
                }
                100% {
                    transform: scale(1);
                }
            }
        `;
    }

    render() {
        return html`
            <div
                id="canvas"
                ?opened=${this.opened}
                ?backdrop=${this.backdrop}>
                <div
                    id="preview_dialog"
                    class=${classMap({
                        autoclose: this.autoClose,
                        transparent: this.transparent,
                        'new-window': this.launchType >= launchTypes.LAUNCHTYPE_NEWWINDOW,
                    })}>
                    ${this.opened
                        ? html`
                            ${choose(this.playerType, [
                                    [0, this._renderUrlPlayer],
                                    [1, this._renderOsPlayer],
                                    [2, this._renderScormPlayer],
                                    [3, this._renderUrlPlayer],
                                ],
                                this._renderUrlPlayer,
                            )}
                        `
                        : nothing
                    }

                    <div
                        class="close"
                        @click=${() => { store.dispatch(closePreview()); }}
                        ?hidden=${this.autoClose}>
                        <sdb-content-icon icon="fal fa-xmark"></sdb-content-icon>
                        <paper-ripple></paper-ripple>
                    </div>
                </div>
            </div>
        `;
    }

    _renderUrlPlayer = () => html`
        <sdb-preview-url-player
            id="iframe_player"
            launchUrl=${this.launchUrl}
            launchType=${this.launchType}
            @close=${() => { store.dispatch(closePreview()); }}>
        </sdb-preview-url-player>
    `;

    _renderOsPlayer = () => html`
        <sdb-os-player
            id="os_player"
            .user=${this.user}
            .settings=${userManager._settings}
            referenceId=${this.launchData?.referenceId}
            elearningId=${this.launchData?.elearningId}
            environment=${this._ensureDevelopment(this.launchData?.environment)}
            @playerExit=${() => { store.dispatch(closePreview()); }}
            browse>
        </sdb-os-player>
    `;

    _renderScormPlayer = () => html`
        <sdb-scorm-preview-player
            id="scorm_player"
            packageId=${this.launchData?.packageId}
            sessionId=${this.launchData?.referenceId}
            launchType=${this.launchType}
            @finish=${() => { store.dispatch(closePreview()); }}>
        </sdb-scorm-preview-player>
    `;

    _ensureDevelopment(env) {
        return env !== 'production' ? 'development' : 'production';
    }
}
